import { ImageIcon } from "lucide-react";
import type React from "react";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/**
 * Framed product screenshot with its caption. Until real captures are added
 * (`src`), it renders `fallback` (a drawn product scene) or a labelled
 * placeholder of the same size.
 */
export function ProductShot({
	alt,
	caption,
	src,
	fallback,
	format = "desktop",
	priority = false,
	className,
}: {
	alt: string;
	caption?: string;
	src?: string;
	fallback?: React.ReactNode;
	format?: "desktop" | "phone";
	/** Above-the-fold image: loaded eagerly. */
	priority?: boolean;
	className?: string;
}) {
	const { t } = useI18n();
	const frame = cn(
		"shot-shadow overflow-hidden rounded-xl border bg-muted",
		format === "phone"
			? "mx-auto aspect-[9/19] w-full max-w-60 rounded-[2rem]"
			: "aspect-[16/10] w-full",
	);

	return (
		<figure className={cn("space-y-4", className)}>
			{src ? (
				<img
					src={src}
					alt={alt}
					loading={priority ? "eager" : "lazy"}
					fetchPriority={priority ? "high" : undefined}
					decoding="async"
					className={cn(frame, "object-cover object-top")}
				/>
			) : fallback ? (
				<div role="img" aria-label={alt}>
					{fallback}
				</div>
			) : (
				<div
					role="img"
					aria-label={alt}
					className={cn(
						frame,
						"dot-grid flex flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground",
					)}
				>
					<ImageIcon aria-hidden="true" className="size-6" />
					<span>{t((t) => t.common.shotPending)}</span>
				</div>
			)}
			{caption ? (
				<figcaption className="flex items-center gap-2 font-mono text-[11px] tracking-[0.08em] text-muted-foreground">
					<span aria-hidden="true" className="text-signal-ink">
						{"//"}
					</span>
					{caption}
				</figcaption>
			) : null}
		</figure>
	);
}
